import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../../lib/api/auth';

export const ForgotPasswordForm: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await authService.forgotPassword(email);
            setIsSuccess(true);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to send reset link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="w-full max-w-md p-8 glass rounded-2xl shadow-xl animate-fade-in text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="w-8 h-8" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_2bf9f70a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2bf9f70a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Check your email</h2>
                <p className="text-gray-600 mb-8">
                    We've sent a password reset link to <span className="font-semibold">{email}</span>.
                </p>
                <Link
                    to="/login"
                    className="btn-primary w-full py-3 rounded-lg text-white font-semibold inline-block"
                >
                    Back to Sign In
                </Link>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md p-8 glass rounded-2xl shadow-xl animate-fade-in">
            <div className="mb-8 text-center">
                <h1 className="text-3xl font-medium bg-clip-text text-transparent bg-gradient-to-r from-accent-primary to-orange-600">
                    Forgot Password
                </h1>
                <p className="text-neutral-500 mt-2">Enter your email to reset your password</p>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded animate-slide-in">
                    <p className="font-medium">Error</p>
                    <p className="text-sm">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Email Address
                    </label>
                    <input
                        type="email"
                        required
                        className="input w-full focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all"
                        placeholder="admin@dashbal.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full btn-primary py-3 rounded-lg text-white font-semibold transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 shadow-lg shadow-accent-primary/20"
                >
                    {isLoading ? (
                        <div className="flex items-center space-x-2 justify-center">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Sending...</span>
                        </div>
                    ) : (
                        'Send Reset Link'
                    )}
                </button>

                <div className="text-center pt-2">
                    <Link to="/login" className="text-sm text-neutral-600 hover:text-accent-primary transition-colors">
                        ← Back to Sign In
                    </Link>
                </div>
            </form>
        </div>
    );
};
